import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    inject,
    input,
    Input,
    model,
    OnDestroy,
    OnInit,
    output,
    Output,
} from '@angular/core';
import {
    auditTime,
    BehaviorSubject,
    combineLatest,
    Observable,
    shareReplay,
    withLatestFrom,
} from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import moment from 'moment';
import { ButtonModule } from '@bazis/shared/components/web/button/button.module';
import { IconModule } from '@bazis/shared/components/web/icon/icon.module';
import { TooltipModule } from '@bazis/shared/components/web/tooltip/tooltip.module';
import { CommonModule } from '@angular/common';
import { ColorDirective } from '@bazis/shared/directives/color.directive';
import { TranslocoModule } from '@jsverse/transloco';
import { BAZIS_IS_MOBILE } from '@bazis/shared/tokens/is-mobile.token';
import {
    BAZIS_DEVICE_ORIENTATION,
    BazisDeviceOrientation,
} from '@bazis/shared/tokens/orientation.token';

export interface BazisTimeLineAction {
    icon: string;
    iconSrc?: string;
    tooltipKey: string;
    code: string;
    active: boolean;
}

@Component({
    selector: 'bazis-timeline',
    templateUrl: './timeline.component.html',
    imports: [
        ButtonModule,
        IconModule,
        TooltipModule,
        NgxSliderModule,
        ColorDirective,
        CommonModule,
        TranslocoModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineComponent implements OnInit, OnDestroy {
    readonly isMobile = inject(BAZIS_IS_MOBILE);
    readonly deviceOrientation$ = inject(BAZIS_DEVICE_ORIENTATION);

    readonly BazisDeviceOrientation = BazisDeviceOrientation;

    readonly showSpeedMultipliers = input(true);

    readonly timelineActions = model<BazisTimeLineAction[]>([]);

    @HostBinding('class') get hostClass() {
        return {
            'bazis-timeline': true,
        };
    }

    basicStep = 150000;

    @Input()
    trackerTimeLine$: Observable<number[]>;

    @Input()
    sliderOptions: Options = {
        floor: 0,
        ceil: 86400000,
        step: this.basicStep,
    };

    timeChange = output();

    trackerLabels$;

    chart$;

    showChart = true;

    dateTimeBorders = [0, 0];

    speedMultiplier$ = new BehaviorSubject(1);

    basicTimeInterval = 400;

    isPlaying$ = new BehaviorSubject(false);

    emitTimeInterval;

    timeToEmit = new TemplateObservable(null);

    timeToEmit$ = this.timeToEmit.$.pipe(shareReplay(SHARE_REPLAY_SETTINGS));

    speedMultipliers = [1, 2, 4];

    playing$ = combineLatest([this.speedMultiplier$, this.isPlaying$]).pipe(
        map(([speedMultiplier, isPlaying]) => {
            this.clearInterval();
            if (!isPlaying) {
                return;
            }
            this.emitTimeInterval = setInterval(() => {
                const next = this.timeToEmit._ + this.basicStep;
                if (next >= this.dateTimeBorders[1]) {
                    this.skipNext = true;
                    this.timeToEmit.set(this.dateTimeBorders[1]);
                    this.clearInterval();
                    this.isPlaying$.next(false);
                    return;
                } else {
                    this.timeToEmit.set(next);
                }
            }, this.basicTimeInterval / speedMultiplier);
            return true;
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    emitting$ = this.timeToEmit$.pipe(
        tap((timeToEmit) => {
            this.timeChange.emit(timeToEmit);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    diffValue = 0;

    moveTrackerToEnd$;

    updateCurrentTime$;

    skipNext = false;

    constructor() {}

    toggleActionState(code: string) {
        this.timelineActions.update((actions) =>
            actions.map((action) => {
                if (action.code === code) {
                    action.active = !action.active;
                }

                return action;
            }),
        );
    }

    ngOnInit() {
        this.trackerLabels$ = this.trackerTimeLine$.pipe(
            map((period) => {
                this.dateTimeBorders = [period[0], period[1]];
                this.diffValue = (period[1] - period[0]) / 100;

                const stepSize = (period[1] - period[0]) / 12;

                this.sliderOptions = {
                    ...this.sliderOptions,
                    floor: this.dateTimeBorders[0],
                    ceil: this.dateTimeBorders[1],
                    showTicks: true,
                    tickStep: stepSize,
                    getLegend: (value: number): string => moment(value).format('HH:mm'),
                    translate: (value: number): string => moment(value).format('HH:mm'),
                };

                const labels = [period[0]];

                const maxIterations = 100;
                let i = 0;

                while (labels[labels.length - 1] + stepSize < period[1] && i < maxIterations) {
                    const next = labels[labels.length - 1] + stepSize;

                    if (!Number.isFinite(next)) {
                        console.error('Next label is not a finite number', next);
                        break;
                    }

                    labels.push(next);
                    i++;
                }

                return labels.map((item) => {
                    const rounded = Math.ceil(item / (60 * 1000)) * 60 * 1000;
                    return moment(rounded).format('HH:mm');
                });
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        this.updateCurrentTime$ = this.trackerLabels$.pipe(
            withLatestFrom(this.isPlaying$),
            tap(([r, isPlaying]) => {
                if (!this.timeToEmit._ || this.timeToEmit._ <= this.dateTimeBorders[0]) {
                    this.timeToEmit.set(this.dateTimeBorders[1]);
                } else {
                    if (
                        !isPlaying &&
                        (this.dateTimeBorders[1] - this.timeToEmit._) /
                            (this.dateTimeBorders[1] - this.dateTimeBorders[0]) <
                            0.05
                    ) {
                        this.skipNext = true;
                        this.timeToEmit.set(this.dateTimeBorders[1]);
                    }
                }
            }),
            debounceTime(0),
            tap((v) => {
                this.skipNext = false;
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    setSpeedMultiplier(value) {
        this.speedMultiplier$.next(value);
    }

    clearInterval() {
        if (this.emitTimeInterval) {
            clearInterval(this.emitTimeInterval);
            this.emitTimeInterval = null;
        }
    }

    ngOnDestroy(): void {
        this.clearInterval();
    }

    setPlaying(play) {
        if (play && this.timeToEmit._ >= this.dateTimeBorders[1]) {
            this.timeToEmit.set(this.dateTimeBorders[0]);
        }
        this.isPlaying$.next(play);
    }

    setValueFromSlider(value, isPlaying = false, speedMultiplier = 0) {
        if (this.skipNext) return;
        if (!isPlaying) {
            this.timeToEmit.set(value);
            return;
        }
        if (Math.abs(value - this.timeToEmit._) > this.diffValue) {
            this.timeToEmit.set(value);
            this.setPlaying(false);
        }
    }
}
